import type { LoomAuthoringDocument, LoomSceneNode } from "./loomAuthoringModel";
import { deriveLoomWingBodyDefinition } from "./loomAuthoritativeDefinition";
import { assertPinnedLoomAirshowPreset } from "./loomAirshowPresetTrust";
import {
  parseLoomPresetReceipt,
  type LoomPresetReceipt,
} from "./loomPresetReceipt";
import {
  assertLoomPresetV3GraphBinding,
  parseLoomPresetV3Binding,
  type LoomPresetV3Binding,
} from "./loomPresetV3Binding";

export type {
  LoomPresetAdaptation,
  LoomPresetNormalizationReceipt,
  LoomPresetOmittedComponent,
  LoomPresetReceipt,
  LoomPresetSelectedComponent,
} from "./loomPresetReceipt";
export type { LoomPresetV3Binding } from "./loomPresetV3Binding";

export interface LoomPresetApplyCommand {
  schema: "loom-authoring-command/v1";
  commandId: string;
  expectedRevision: number;
  kind: "preset.apply";
  nodes: readonly LoomSceneNode[];
  presetBinding?: LoomPresetV3Binding;
  receipt: LoomPresetReceipt;
  rootNodeIds: readonly string[];
}

type CommandBase = Pick<LoomPresetApplyCommand, "schema" | "commandId" | "expectedRevision">;
type ReplacementParser = (
  nodes: unknown,
  rootNodeIds: unknown,
) => { nodes: readonly LoomSceneNode[]; rootNodeIds: readonly string[] };
type UnknownRecord = Record<string, unknown>;

function assertV2PresetGraph(nodes: readonly LoomSceneNode[], rootNodeIds: readonly string[]): void {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  if (nodes.length !== 4 || rootNodeIds.length !== 1 || rootNodeIds[0] !== "vehicle") {
    throw new Error("preset.apply requires the stable four-node wing/body graph");
  }
  const wing = byId.get("wing");
  const fuselage = byId.get("fuselage");
  const partner = byId.get("wing-partner");
  const vehicle = byId.get("vehicle");
  if (wing?.type !== "primitive" || wing.definition.primitive !== "wing"
      || fuselage?.type !== "primitive" || fuselage.definition.primitive !== "fuselage"
      || partner?.type !== "transform" || partner.inputNodeId !== "wing"
      || partner.operation.transform !== "mirror" || partner.operation.axis !== "y"
      || partner.operation.planeOffsetM !== 0 || vehicle?.type !== "boolean"
      || vehicle.operation !== "union" || vehicle.inputNodeIds.length !== 3
      || !["fuselage", "wing", "wing-partner"].every((id) => vehicle.inputNodeIds.includes(id))) {
    throw new Error("preset.apply replacement does not match the stable wing/body graph contract");
  }
}

export function parseLoomPresetApplyCommand(
  value: UnknownRecord,
  base: CommandBase,
  parseReplacement: ReplacementParser,
): LoomPresetApplyCommand {
  const replacement = parseReplacement(value.nodes, value.rootNodeIds);
  const receipt = parseLoomPresetReceipt(value.receipt);
  if (value.presetBinding === undefined) {
    assertV2PresetGraph(replacement.nodes, replacement.rootNodeIds);
    const definition = deriveLoomWingBodyDefinition({
      schema: "loom-authoring/v1",
      documentId: "preset-replacement",
      revision: 0,
      nodes: replacement.nodes,
      rootNodeIds: replacement.rootNodeIds,
    } satisfies LoomAuthoringDocument);
    if (!definition) {
      throw new Error("preset.apply replacement is not an authoritative wing/body definition");
    }
    assertPinnedLoomAirshowPreset(receipt, definition);
    return {
      ...base, kind: "preset.apply", nodes: replacement.nodes,
      receipt, rootNodeIds: replacement.rootNodeIds,
    };
  }
  const presetBinding = parseLoomPresetV3Binding(value.presetBinding);
  assertLoomPresetV3GraphBinding(replacement.nodes, replacement.rootNodeIds, presetBinding, receipt);
  return {
    ...base,
    kind: "preset.apply",
    nodes: replacement.nodes,
    presetBinding,
    receipt,
    rootNodeIds: replacement.rootNodeIds,
  };
}
